import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { InvestmentsAPI, Investment, InvestmentCategory, InvestmentStatus } from '../services/investments.ts';
import { formatCurrency, getCurrencyLocale } from '../utils/currency.ts';
import { formatStatus, getStatusColor } from '../utils/statusFormatter.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import ConfirmationModal from '../components/ConfirmationModal.tsx';
import { PlusIcon, MagnifyingGlassIcon, BanknotesIcon, EyeIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

const categoryLabels: Record<InvestmentCategory, string> = {
  crypto: 'Cripto',
  offshore: 'Offshore',
  stocks: 'Ações',
  fixed_income: 'Renda Fixa',
  funds: 'Fundos',
  other: 'Outros',
};

const Investments: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState<'all' | InvestmentCategory>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | InvestmentStatus>('active');
  const [filterCurrency, setFilterCurrency] = useState<'all' | string>('all');

  const { data: investments = [], isLoading, error, refetch } = useQuery<Investment[]>(['investments'], InvestmentsAPI.list, {
    staleTime: 1000 * 60 * 5,
  });

  const deleteMutation = useMutation(async (id: string) => InvestmentsAPI.remove(id), {
    onSuccess: () => {
      // Atualiza a lista e fecha o modal
      queryClient.invalidateQueries(['investments']);
      refetch();
      setDeleteModal({ isOpen: false, investmentId: '', investmentDescription: '' });
    },
  });

  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; investmentId: string; investmentDescription: string }>({
    isOpen: false,
    investmentId: '',
    investmentDescription: '',
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const safeNumber = (value: any): number => {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  const filteredInvestments = useMemo(() => {
    return investments.filter(i => {
      const matchesSearch =
        i.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.category.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = filterCategory === 'all' || i.category === filterCategory;
      const matchesStatus = filterStatus === 'all' || i.status === filterStatus;
      const currency = (i as any).currency || 'BRL';
      const matchesCurrency = filterCurrency === 'all' || currency === filterCurrency;
      return matchesSearch && matchesCategory && matchesStatus && matchesCurrency;
    });
  }, [investments, searchTerm, filterCategory, filterStatus, filterCurrency]);

  const summary = useMemo(() => {
    const byCurrency: Record<string, { invested: number; current: number; yield: number; yieldPercent: number }> = {};
    const countsByCurrency: Record<string, number> = {};
    filteredInvestments.forEach(i => {
      const c = (i as any).currency || 'BRL';
      const invested = safeNumber(i.investedValue);
      const current = safeNumber(i.currentValue);
      const y = current - invested;
      const yp = invested > 0 ? (y / invested) * 100 : 0;
      const prev = byCurrency[c] || { invested: 0, current: 0, yield: 0, yieldPercent: 0 };
      const newInvested = prev.invested + invested;
      const newCurrent = prev.current + current;
      const newYield = prev.yield + y;
      const newYieldPercent = newInvested > 0 ? (newYield / newInvested) * 100 : 0;
      byCurrency[c] = { invested: newInvested, current: newCurrent, yield: newYield, yieldPercent: newYieldPercent };
      countsByCurrency[c] = (countsByCurrency[c] || 0) + 1;
    });
    return { total: filteredInvestments.length, byCurrency, countsByCurrency } as const;
  }, [filteredInvestments]);

  const handleViewDetails = (id: string) => navigate(`/investments/${id}`);
  const handleEdit = (id: string) => navigate(`/investments/${id}/edit`);
  const handleDelete = (id: string, description: string) => setDeleteModal({ isOpen: true, investmentId: id, investmentDescription: description });
  const confirmDelete = async () => {
    if (!deleteModal.investmentId) return;
    setIsDeleting(true);
    try {
      await deleteMutation.mutateAsync(deleteModal.investmentId);
    } catch (e) {
      console.error('Erro ao excluir investimento:', e);
    } finally {
      setIsDeleting(false);
    }
  };
  const closeDeleteModal = () => {
    if (!isDeleting) setDeleteModal({ isOpen: false, investmentId: '', investmentDescription: '' });
  };

  // Summary already computed from filteredInvestments above

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return <div className="text-center text-red-500">Erro ao carregar investimentos. Por favor, tente novamente.</div>;
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <BanknotesIcon className="h-8 w-8 mr-3 text-primary-600" />
            Investimentos
          </h1>
          <p className="text-gray-600 mt-2">Gerencie seus investimentos</p>
        </div>
        <button onClick={() => navigate('/investments/create')} className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-medium transition-colors flex items-center">
          <PlusIcon className="h-5 w-5 mr-2" />
          Novo Investimento
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BanknotesIcon className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total de Investimentos</p>
              <p className="text-2xl font-bold text-gray-900">{summary.total}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {Object.entries(summary.countsByCurrency).map(([c, count]) => (
                  <span key={c} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {c}: {count}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
        {Object.entries(summary.byCurrency).map(([c, vals]) => (
          <div key={c} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BanknotesIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{`Totais (${c})`}</p>
                <p className="text-sm text-gray-600">Investido</p>
                <p className="text-xl font-bold text-gray-900">{formatCurrency(vals.invested, c, getCurrencyLocale(c))}</p>
                <p className="mt-2 text-sm text-gray-600">Atual</p>
                <p className="text-xl font-bold text-gray-900">{formatCurrency(vals.current, c, getCurrencyLocale(c))}</p>
                <p className="mt-2 text-sm text-gray-600">Rendimento</p>
                <p className="text-xl font-bold text-gray-900">{formatCurrency(vals.yield, c, getCurrencyLocale(c))} ({vals.yieldPercent.toFixed(2)}%)</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filtros e Busca */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input type="text" placeholder="Buscar investimentos..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              {/* Category Filter */}
              <select value={filterCategory} onChange={e => setFilterCategory(e.target.value as any)} className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[160px]">
                <option value="all">Todas as categorias</option>
                <option value="stocks">Ações</option>
                <option value="fixed_income">Renda Fixa</option>
                <option value="funds">Fundos</option>
                <option value="crypto">Cripto</option>
                <option value="offshore">Offshore</option>
                <option value="other">Outros</option>
              </select>

              {/* Status Filter */}
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value as any)} className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[160px]">
                <option value="all">Todos os status</option>
                <option value="active">Ativo</option>
                <option value="sold">Vendido</option>
              </select>

              {/* Currency Filter */}
              <select value={filterCurrency} onChange={e => setFilterCurrency(e.target.value as any)} className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[140px]">
                <option value="all">Todas as moedas</option>
                {Array.from(new Set(investments.map(i => (i as any).currency || 'BRL'))).map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

      {/* Grid de Investimentos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredInvestments.map(i => (
          <div key={i.id} className="bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 overflow-hidden">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{i.description}</h3>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">{categoryLabels[i.category]}</span>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(i.status)}`}>{formatStatus(i.status)}</span>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Valor Investido:</span>
                  <span className="text-sm font-medium text-gray-900">{formatCurrency(i.investedValue, (i as any).currency || 'BRL', getCurrencyLocale((i as any).currency || 'BRL'))}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Valor Atual:</span>
                  <span className="text-sm font-medium text-gray-900">{formatCurrency(i.currentValue, (i as any).currency || 'BRL', getCurrencyLocale((i as any).currency || 'BRL'))}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Rendimento:</span>
                  <span className="text-sm font-medium text-gray-900">{formatCurrency(i.yieldAmount, (i as any).currency || 'BRL', getCurrencyLocale((i as any).currency || 'BRL'))} ({(i.yieldPercent || 0).toFixed(2)}%)</span>
                </div>
              </div>

              <div className="flex gap-2">
                <button onClick={() => handleViewDetails(i.id)} className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-200 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200">
                  <EyeIcon className="h-4 w-4 mr-1" />
                  Ver
                </button>
                <button onClick={() => handleEdit(i.id)} className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-primary-200 text-sm font-medium rounded-xl text-primary-700 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200">
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Editar
                </button>
                {hasPermission('investments.delete') && (
                  <button onClick={() => handleDelete(i.id, i.description)} className="px-3 py-2 border border-red-200 text-sm font-medium rounded-xl text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200">
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredInvestments.length === 0 && (
        <div className="text-center py-12">
          <BanknotesIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum investimento encontrado</h3>
          <p className="mt-1 text-sm text-gray-500">{searchTerm || filterCategory !== 'all' || filterStatus !== 'all' ? 'Tente ajustar os filtros de busca.' : 'Comece criando um novo investimento.'}</p>
          {!searchTerm && filterCategory === 'all' && filterStatus === 'all' && (
            <div className="mt-6">
              <button onClick={() => navigate('/investments/create')} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700">
                <PlusIcon className="h-5 w-5 mr-2" />
                Novo Investimento
              </button>
            </div>
          )}
        </div>
      )}

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title="Excluir Investimento"
        message={`Tem certeza que deseja excluir o investimento "${deleteModal.investmentDescription}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Investments;
